/* Centralised API client — automatically attaches JWT and handles errors */

const BASE = '/api';

const getToken = () => localStorage.getItem('ti_token');

const h = (extra = {}) => ({
  'Content-Type': 'application/json',
  ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
  ...extra,
});

async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, options);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

/* ── Auth ─────────────────────────────────── */
export const authAPI = {
  health:   ()     => request('/health'),
  register: (data) => request('/auth/register', { method: 'POST', headers: h(), body: JSON.stringify(data) }),
  login:    (data) => request('/auth/login',    { method: 'POST', headers: h(), body: JSON.stringify(data) }),
  me:       ()     => request('/auth/me',        { headers: h() }),
};

/* ── Firm ─────────────────────────────────── */
export const firmAPI = {
  get:    ()     => request('/firm',            { headers: h() }),
  update: (data) => request('/firm', { method: 'PUT', headers: h(), body: JSON.stringify(data) }),
};

/* ── Members ──────────────────────────────── */
export const membersAPI = {
  list:   ()         => request('/members',        { headers: h() }),
  add:    (data)     => request('/members', { method: 'POST',   headers: h(), body: JSON.stringify(data) }),
  update: (id, data) => request(`/members/${id}`, { method: 'PATCH',  headers: h(), body: JSON.stringify(data) }),
  remove: (id)       => request(`/members/${id}`, { method: 'DELETE', headers: h() }),
};

/* ── Signatories ──────────────────────────── */
export const signatoriesAPI = {
  list:      ()         => request('/signatories',        { headers: h() }),
  add:       (data)     => request('/signatories', { method: 'POST',   headers: h(), body: JSON.stringify(data) }),
  update:    (id, data) => request(`/signatories/${id}`, { method: 'PATCH',  headers: h(), body: JSON.stringify(data) }),
  remove:    (id)       => request(`/signatories/${id}`, { method: 'DELETE', headers: h() }),
  setDefault:(id)       => request(`/signatories/${id}`, { method: 'PATCH',  headers: h(), body: JSON.stringify({ is_default: true }) }),
};

/* ── Transactions / Ledger ───────────────── */
export const transactionsAPI = {
  list:    (params = {}) => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
    const q = new URLSearchParams(cleanParams).toString();
    return request(`/transactions${q ? '?' + q : ''}`, { headers: h() });
  },
  balance: ()     => request('/transactions/balance',          { headers: h() }),
  add:     (data) => request('/transactions',  { method: 'POST',   headers: h(), body: JSON.stringify(data) }),
  remove:  (id)   => request(`/transactions/${id}`, { method: 'DELETE', headers: h() }),
};

/* ── Contracts ───────────────────────────── */
export const contractsAPI = {
  list:   ()         => request('/contracts',              { headers: h() }),
  get:    (id)       => request(`/contracts/${id}`,        { headers: h() }),
  create: (data)     => request('/contracts',  { method: 'POST',   headers: h(), body: JSON.stringify(data) }),
  update: (id, data) => request(`/contracts/${id}`, { method: 'PUT',    headers: h(), body: JSON.stringify(data) }),
  remove: (id)       => request(`/contracts/${id}`, { method: 'DELETE', headers: h() }),
  stats:  ()         => request('/contracts/stats/summary', { headers: h() }),
  createItem: (contractId, resource, data) =>
    request(`/contracts/${contractId}/${resource}`, { method: 'POST', headers: h(), body: JSON.stringify(data) }),
  updateItem: (contractId, resource, itemId, data) =>
    request(`/contracts/${contractId}/${resource}/${itemId}`, { method: 'PUT', headers: h(), body: JSON.stringify(data) }),
  removeItem: (contractId, resource, itemId) =>
    request(`/contracts/${contractId}/${resource}/${itemId}`, { method: 'DELETE', headers: h() }),
};

/* ── Invoices ─────────────────────────────── */
export const invoicesAPI = {
  list:       ()         => request('/invoices',              { headers: h() }),
  save:       (data)     => request('/invoices', { method: 'POST',   headers: h(), body: JSON.stringify(data) }),
  remove:     (id)       => request(`/invoices/${id}`,        { method: 'DELETE', headers: h() }),
  nextSeq:    (fy)       => request('/invoices/next-seq',     { method: 'POST', headers: h(), body: JSON.stringify({ fiscal_year: fy }) }),
  stats:      ()         => request('/invoices/stats',        { headers: h() }),
  setPayment: (id, data) => request(`/invoices/${id}/payment`,{ method: 'PATCH', headers: h(), body: JSON.stringify(data) }),
};
